<template>
  <section class="panel" aria-label="License Information">
    <div class="panel__title">
      {{ $t('licensing.settings.info.title') }}
      <span class="panel__title-actions">
        <span
          v-if="licenseRows.length <= 1 && isExpired"
          class="status-badge status-badge--expired"
        >
          {{ $t('licensing.settings.expired') }}
        </span>
        <span v-else-if="licenseRows.length <= 1 && isExpiringSoon"
          class="status-badge status-badge--warning"
        >
          {{ daysUntilExpiry }} {{ $t('licensing.settings.days.left') }}
        </span>
      </span>
    </div>

    <!-- Single license: show flat -->
    <div v-if="licenseRows.length <= 1" class="panel__fields">
      <div class="panel__row">
        <span class="panel__label">{{ $t('licensing.settings.info.tenant') }}</span>
        <span class="panel__value">{{ tenantName }}</span>
      </div>
      <div class="panel__row">
        <span class="panel__label">{{ $t('licensing.settings.info.product') }}</span>
        <span class="panel__value">{{ productLabel }}</span>
      </div>
      <div class="panel__row">
        <span class="panel__label">{{ $t('licensing.settings.info.type') }}</span>
        <span class="panel__value">{{ licenseTypeLabel }}</span>
      </div>
      <div class="panel__row">
        <span class="panel__label">{{ $t('licensing.settings.info.duration') }}</span>
        <span class="panel__value">{{ durationLabel }}</span>
      </div>
      <div class="panel__row">
        <span class="panel__label">{{ $t('licensing.settings.info.created') }}</span>
        <span class="panel__value">{{ formatDate(license.createdDate) }}</span>
      </div>
    </div>

    <!-- Multiple licenses: accordion -->
    <div v-else class="license-accordion">
      <div class="license-accordion__head">
        <span class="license-accordion__head-spacer"></span>
        <span class="license-accordion__period license-accordion__col-label">
          Expiration Date
        </span>
        <span class="license-accordion__status"></span>
      </div>
      <div
        v-for="row in licenseRows"
        :key="row.id"
        class="license-accordion__item"
        :class="{ 'license-accordion__item--expired': row.isExpired }"
      >
        <component
          :is="isStaticRow(row) ? 'div' : 'button'"
          class="license-accordion__header"
          :class="{ 'license-accordion__header--static': isStaticRow(row) }"
          :aria-expanded="!isStaticRow(row) && expandedId === row.id ? 'true' : 'false'"
          @click="!isStaticRow(row) && toggleLicense(row.id)"
        >
          <v-icon
            v-if="!isStaticRow(row)"
            small
            class="license-accordion__chevron"
            :class="{ 'license-accordion__chevron--open': expandedId === row.id }"
          >
            chevron_right
          </v-icon>
          <span class="license-accordion__name">{{ row.displayName }}</span>
          <span class="license-accordion__period">{{ getRowPeriod(row) }}</span>
          <span class="license-accordion__status">
            <span
              v-if="isConfigRow(row)"
              class="status-badge status-badge--active"
            >
              Active
            </span>
            <template v-else>
              <span
                v-if="!row.isExpired && getRowDaysLeft(row) <= 30"
                class="status-badge status-badge--warning"
              >
                {{ getRowDaysLeft(row) }}d left
              </span>
              <span
                class="status-badge"
                :class="row.isExpired ? 'status-badge--expired' : 'status-badge--active'"
              >
                {{ row.isExpired ? 'Expired' : 'Active' }}
              </span>
            </template>
          </span>
        </component>
        <div v-if="expandedId === row.id && !isStaticRow(row)" class="license-accordion__body">
          <div class="panel__row">
            <span class="panel__label">{{ $t('licensing.settings.info.type') }}</span>
            <span class="panel__value">{{ getRowTypeLabel(row) }}</span>
          </div>
          <div v-for="r in getRowRestrictions(row)" :key="r.label" class="panel__row">
            <span class="panel__label">{{ r.label }}</span>
            <span class="panel__value">{{ r.value }}</span>
          </div>
        </div>
      </div>
    </div>

    <div class="panel__actions" :class="{ 'panel__actions--expired': isExpired }">
      <div v-if="isExpired" class="panel__expired-cta">
        <div class="panel__expired-cta__content">
          <span class="panel__expired-cta__msg">
            {{ $t('licensing.settings.expired.subtitle') }}
          </span>
          <v-btn
            small
            color="primary"
            class="activate-btn"
            @click="$emit('activate')"
          >
            <v-icon small left>vpn_key</v-icon>
            {{ $t('licensing.settings.activate') }}
          </v-btn>
        </div>
        <lottie-animation
          ref="expiredAnim"
          :animationData="require('@/assets/icons/animations/expired.json')"
          :loop="true"
          class="panel__expired-cta__anim"
        />
      </div>
      <v-btn
        v-else
        small
        color="primary"
        class="activate-btn"
        @click="$emit('activate')"
      >
        <v-icon small left>add</v-icon>
        {{ $t('licensing.settings.activate') }}
      </v-btn>
    </div>
  </section>
</template>

<script>
import LottieAnimation from 'lottie-web-vue';

const LICENSE_TYPE_LABELS = {
  pov_poc: 'Trial',
  mssp: 'MSSP',
};

// Modules that behave like on/off feature flags — no tier or usage limits,
// so the UI shows "Enabled till <date>" instead of an asset/keyword figure.
const FEATURE_FLAG_MODULES = ['cloud_compliance', 'threat_canvas', 'dashboard_date_filter'];

function getTypeLabel(startsAt, expiresAt, licenseType) {
  if (LICENSE_TYPE_LABELS[licenseType]) return LICENSE_TYPE_LABELS[licenseType];
  if (!startsAt || !expiresAt) return '—';
  const diffDays = Math.round(
    (new Date(expiresAt) - new Date(startsAt)) / (1000 * 60 * 60 * 24),
  );
  if (diffDays <= 31) return '1 Month';
  if (diffDays <= 62) return '2 Months';
  if (diffDays <= 93) return '3 Months';
  if (diffDays <= 186) return '6 Months';
  if (diffDays >= 350 && diffDays <= 380) return '1 Year';
  if (diffDays > 380 && diffDays <= 740) return '2 Years';
  const months = Math.round(diffDays / 30);
  if (months >= 12) {
    const years = Math.round(months / 12);
    return `${years} Year${years > 1 ? 's' : ''}`;
  }
  return `${months} Months`;
}

export default {
  name: 'LicenseInfoPanel',
  components: { LottieAnimation },
  props: {
    license: { type: Object, required: true },
    licenseRows: { type: Array, required: true },
    tenantName: { type: String, default: '—' },
    productLabel: { type: String, default: 'CTEM' },
    daysUntilExpiry: { type: Number, default: 0 },
    isExpired: { type: Boolean, default: false },
    isExpiringSoon: { type: Boolean, default: false },
  },
  data() {
    return { expandedId: null };
  },
  computed: {
    licenseTypeLabel() {
      return getTypeLabel(
        this.license.startsAt,
        this.license.expiresAt,
        this.license.licenseType,
      );
    },
    durationLabel() {
      if (!this.license.startsAt || !this.license.expiresAt) return '—';
      return `${this.formatDate(this.license.startsAt)} — ${this.formatDate(this.license.expiresAt)}`;
    },
  },
  methods: {
    formatDate(dateStr) {
      if (!dateStr) return '—';
      return new Date(dateStr).toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      });
    },
    getRowTypeLabel(row) {
      return getTypeLabel(row.startsAt, row.expiresAt, row.licenseType);
    },
    getRowRestrictions(row) {
      const d = row.skuDetails;
      if (!d) return [];
      const items = [];
      if (d.maxAssets != null) items.push({ label: 'Max Assets', value: d.maxAssets.toLocaleString() });
      if (d.keywords != null) items.push({ label: 'Keywords', value: String(d.keywords) });
      if (d.maxDomains != null) items.push({ label: 'Max Domains', value: String(d.maxDomains) });
      if (d.scanLimits) {
        Object.entries(d.scanLimits).forEach(([key, val]) => {
          const label = key.replace(/([A-Z])/g, ' $1').replace(/^./, s => s.toUpperCase());
          items.push({ label, value: String(val) });
        });
      }
      if (d.includedModules && d.includedModules.length > 0) {
        d.includedModules.forEach(mod => {
          let val = mod.displayName;
          if (mod.keywords != null) val += ` — ${mod.keywords} keywords`;
          if (mod.maxDomains != null) val += ` — ${mod.maxDomains} domains`;
          items.push({ label: 'Includes', value: val });
        });
      }
      return items;
    },
    getRowDaysLeft(row) {
      if (!row.expiresAt) return 999;
      return Math.ceil(
        (new Date(row.expiresAt).getTime() - Date.now()) / (1000 * 60 * 60 * 24),
      );
    },
    isConfigRow(row) {
      return row.skuDetails?.module === 'platform_access' || row.skuDetails?.module === 'mssp';
    },
    isFeatureFlagRow(row) {
      return FEATURE_FLAG_MODULES.includes(row.skuDetails?.module);
    },
    // Static rows render without an expandable body: config markers and
    // feature-flag modules (no tier/limits to reveal).
    isStaticRow(row) {
      return this.isConfigRow(row) || this.isFeatureFlagRow(row);
    },
    // Per-row expiry date. The column header ("Expiration Date") provides the
    // context, so the row shows just the date. Config rows show nothing.
    getRowPeriod(row) {
      if (this.isConfigRow(row)) return '';
      return this.formatDate(row.expiresAt);
    },
    toggleLicense(id) {
      this.expandedId = this.expandedId === id ? null : id;
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/styles/_sizing";
@import "@/assets/styles/_colors";
@import "@/assets/styles/_semantic-sizing";

.panel {
  background: var(--bg-secondary);
  border: $border-width-thin solid var(--border-primary);
  border-radius: $radius-lg;
  padding: $space-5 $space-6;

  &__title {
    color: var(--text-primary);
    font-family: $font-family-bold;
    font-size: $font-size-base;
    margin-bottom: $space-4;
    padding-bottom: $space-2;
    border-bottom: $border-width-thin solid var(--border-primary);
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  &__title-actions {
    display: flex;
    align-items: center;
    gap: $space-2;
    margin-left: auto;
  }

  &__fields {
    display: flex;
    flex-direction: column;
    gap: $space-2-5;
  }

  &__row {
    display: flex;
    align-items: baseline;
    gap: $space-3;
  }

  &__label {
    color: var(--text-secondary);
    font-family: $font-family-regular;
    font-size: $font-size-sm;
    text-transform: uppercase;
    letter-spacing: $letter-spacing-wider;
    min-width: 110px;
    flex-shrink: 0;
  }

  &__value {
    color: var(--text-primary);
    font-family: $font-family-regular;
    font-size: $font-size-base;
  }

  &__actions {
    margin-top: $space-4;
    padding-top: $space-3;
    border-top: $border-width-thin solid var(--border-primary);

    &--expired {
      padding-top: $space-4;
    }
  }
}

.panel__expired-cta {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: $space-4;

  &__anim {
    flex-shrink: 0;
    width: 120px;
    height: 120px;
  }

  &__content {
    display: flex;
    flex-direction: column;
    gap: $space-2;
  }

  &__msg {
    font-family: $font-family-regular;
    font-size: $font-size-sm;
    color: var(--text-secondary);
  }
}

.activate-btn {
  text-transform: none;
  letter-spacing: $letter-spacing-normal;
}

.license-accordion {
  display: flex;
  flex-direction: column;
  gap: $space-1-5;

  &__item {
    border: $border-width-thin solid var(--border-primary);
    border-radius: $radius-6;
    overflow: hidden;

    &--expired { opacity: 0.7; }
  }

  &__header {
    width: 100%;
    display: flex;
    align-items: center;
    gap: $space-2;
    padding: $space-2-5 $space-3;
    background: none;
    border: none;
    cursor: pointer;
    font-family: $font-family-regular;
    font-size: $font-size-base;
    color: var(--text-primary);
    text-align: left;

    &:hover { background: var(--bg-tertiary, $gray-100); }

    &--static {
      cursor: default;
      &:hover { background: none; }
    }

    &:focus-visible {
      outline: $border-width-medium solid var(--brand-primary);
      outline-offset: -#{$border-width-medium};
    }
  }

  &__chevron {
    transition: transform $transition-duration-fast $transition-timing-ease;
    color: var(--text-secondary);

    &--open { transform: rotate(90deg); }
  }

  &__name {
    flex: 1;
    font-family: $font-family-bold;
  }

  &__period {
    color: var(--text-secondary);
    font-family: $font-family-regular;
    font-size: $font-size-sm;
    white-space: nowrap;
    text-align: center;
    min-width: 9rem;
    flex-shrink: 0;
  }

  &__status {
    display: inline-flex;
    justify-content: flex-end;
    align-items: center;
    gap: $space-2;
    min-width: 72px;
    flex-shrink: 0;
  }

  &__head {
    display: flex;
    align-items: center;
    gap: $space-2;
    padding: 0 $space-3 $space-1;
  }

  &__head-spacer {
    flex: 1;
  }

  &__col-label {
    font-size: $font-size-xs;
    text-transform: uppercase;
    letter-spacing: $letter-spacing-wider;
  }

  &__body {
    padding: $space-2 $space-3 $space-3 $space-9;
    display: flex;
    flex-direction: column;
    gap: $space-2;
    border-top: $border-width-thin solid var(--border-primary);
  }
}

.status-badge {
  display: inline-block;
  font-size: $font-size-xs;
  padding: $space-0-5 $space-2-5;
  border-radius: $radius-md;
  font-family: $font-family-bold;
  white-space: nowrap;

  &--expired { background: $error-50; color: $error-500; }
  &--warning { background: $warning-50; color: $warning-500; }
  &--active { background: $success-50; color: $success-500; }
}
</style>
